import { useEffect, useRef, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';
import { getScheduledAmount } from '@actual-app/core/shared/schedules';

import { aqlQuery } from '#queries/aqlQuery';

import type { AgendadoDoMes } from './contasFixas';
import type { Escopo } from './escopo';
import { ehDoEscopo } from './escopo';
import type { LinhaDoMes } from './mesFinanceiro';

type Retorno = {
  agendados: AgendadoDoMes[];
  carregando: boolean;
  /**
   * ⚠️⚠️ FIX ROUND 1 (Finding 3, Important) — antes desta correção, uma
   * falha na consulta de `schedules` era indistinguível de "o dono nunca
   * cadastrou conta fixa nenhuma": as duas caíam em `agendados: []`. Quem
   * já cadastrou e vê a mensagem "Cadastre em Agendados…" (por causa de
   * uma falha de rede, por exemplo) recebe uma instrução ERRADA — repetir
   * um passo que ele já fez. `erro` existe pra `ContasDoMes.tsx` poder
   * distinguir os dois casos, seguindo o mesmo princípio que
   * `useMesFinanceiro.ts` já documenta: falha de leitura NUNCA pode virar
   * "está tudo zerado" na tela.
   */
  erro: Error | null;
};

type AcaoBruta = { op?: unknown; field?: unknown; value?: unknown };

/**
 * Texto de nota que a REGRA do agendado grava nas transações que ela cria
 * — a única forma NATIVA de um `schedule` carregar a tag `#empresa`, já
 * que a tabela `schedules` não tem campo de nota/descrição próprio (ver
 * `packages/loot-core/src/server/aql/schema/index.ts`: `schedules` não
 * lista `notes` entre os campos, diferente de `transactions`).
 *
 * Cobre as DUAS formas de uma regra escrever nota (ver
 * `loot-core/src/types/models/rule.ts`): `{op:'set', field:'notes',
 * value}` (o campo aparece direto no editor de regra) e
 * `{op:'append-notes'|'prepend-notes', value}` (as ações dedicadas de
 * nota). Concatena se houver mais de uma (raro, mas a regra permite).
 *
 * `null` = a regra não diz nada sobre nota — NÃO significa "é pessoal",
 * significa "sem sinal nenhum"; quem decide o que fazer com `null` é
 * `agendadoPertenceAoEscopo`, abaixo.
 */
export function notasDoAgendado(actions: unknown): string | null {
  if (!Array.isArray(actions)) return null;

  const textos: string[] = [];
  for (const bruta of actions as AcaoBruta[]) {
    if (!bruta || typeof bruta !== 'object') continue;
    const acao = bruta as AcaoBruta;
    const viaSetNotes = acao.op === 'set' && acao.field === 'notes';
    const viaAcaoDeNota =
      acao.op === 'append-notes' || acao.op === 'prepend-notes';
    if ((viaSetNotes || viaAcaoDeNota) && typeof acao.value === 'string') {
      textos.push(acao.value);
    }
  }
  return textos.length > 0 ? textos.join(' ') : null;
}

/**
 * ⚠️⚠️ FIX ROUND 1 (Finding 1, Critical) — decide se um agendado pertence
 * ao `escopo` pedido, na ORDEM de evidência disponível (a tabela
 * `schedules` não tem nota própria — ver `notasDoAgendado` acima):
 *
 * 1. A REGRA do agendado diz explicitamente (`notasDaRegra !== null`) —
 *    essa é a única forma de saber o escopo de uma conta fixa que NUNCA
 *    foi paga. Decide sozinha, vence qualquer outro sinal.
 * 2. Sem sinal na regra, mas o agendado JÁ TEM alguma transação neste mês
 *    (`linhasDoAgendado`) — usa a nota DESSAS transações: se QUALQUER uma
 *    bate com o escopo pedido, inclui (generoso em caso de mês misto); se
 *    NENHUMA bate, é claramente do outro escopo neste mês, exclui.
 * 3. Sem regra e sem transação este mês — não há evidência NENHUMA.
 *    ⛔ Isto NÃO finge que filtra: aplica a MESMA regra-padrão que
 *    `escopo.ts` já usa pra qualquer nota ausente ("nota sem a tag é
 *    PESSOAL") — `ehDoEscopo(null, escopo)`. Isso é uma LIMITAÇÃO
 *    conhecida e documentada no relatório da Task 10, não um filtro
 *    completo: uma conta fixa `#empresa` que nunca foi paga E cuja regra
 *    não marca a nota fica, por padrão, visível também no escopo
 *    'pessoal' (mas some do 'empresa', pela mesma regra).
 */
export function agendadoPertenceAoEscopo(
  notasDaRegra: string | null,
  linhasDoAgendado: LinhaDoMes[],
  escopo: Escopo,
): boolean {
  if (escopo === 'tudo') return true;
  if (notasDaRegra !== null) return ehDoEscopo(notasDaRegra, escopo);
  if (linhasDoAgendado.length === 0) return ehDoEscopo(null, escopo);
  return linhasDoAgendado.some(l => ehDoEscopo(l.notes, escopo));
}

/**
 * Busca os AGENDADOS (schedules nativos do Actual) candidatos a aparecer em
 * "Contas do mês" (Task 10, `inicio/ContasDoMes.tsx`).
 *
 * ⚠️⚠️ NÃO É "arquivo de dado" da Task 10 — é a peça de fiação necessária pra
 * cumprir o Step 5 do brief ("passar `totalPrevisto(contas)` como
 * `extras.fixasPrevistas` ao `resumirMes`"), que não existiria em lugar
 * nenhum sem um hook que traga `AgendadoDoMes[]` do banco. Ver o relatório
 * da Task 10 pra essa divergência do "Files" do brief (que só listava
 * `Inicio.tsx` como arquivo a modificar) — decisão registrada, não
 * silenciosa, e aceita no fix round 1.
 *
 * ⚠️⚠️ "Candidato ao mês" NÃO é só `next_date` dentro da janela. Uma conta
 * fixa JÁ PAGA neste mês costuma ter `next_date` empurrado pro PRÓXIMO
 * vencimento assim que é quitada (ex.: paga em 05/09, `next_date` já mostra
 * 05/10) — filtrar só por `next_date` faria a linha "✓ paga" SUMIR da lista
 * assim que fosse quitada, contrariando o wireframe do spec §8.4
 * ("✓ LM Transportes 3.584 3.584"). Por isso o critério de CANDIDATURA (a
 * query) é a UNIÃO: `next_date` dentro do mês OU o `id` já aparece como
 * `schedule` em alguma `linhaDoMes` deste mês.
 *
 * ⚠️ De propósito o filtro de CANDIDATURA (`idsPagos`, a query) NÃO é
 * escopado — só o filtro FINAL de inclusão (`agendadoPertenceAoEscopo`,
 * aplicado sobre `brutos` depois da resposta) é. Escopar a candidatura
 * cedo demais arrisca EXCLUIR da consulta um agendado que pertence de
 * verdade a este escopo só porque uma transação incomum dele veio com nota
 * trocada — sub-buscar é pior que buscar de mais e filtrar depois.
 *
 * ⚠️⚠️ `valorPrevisto` sai do valor CONFIGURADO NO PRÓPRIO AGENDADO (a
 * condição `_amount` da regra do schedule — o número que o dono digitou ao
 * criar "Verisure, R$269, todo dia 20"), via `getScheduledAmount` (a MESMA
 * função que `SchedulesTable.tsx`/`Balance.tsx`/`MobileSchedulesPage.tsx`/
 * `SchedulesListItem.tsx` já usam) — NÃO do orçamento por categoria
 * (`reflect_budgets`, só existente em modo `tracking`).
 *
 * Isso é uma DIVERGÊNCIA DELIBERADA do texto do brief da Task 10 ("a coluna
 * `planejado` sai do orçamento nativo em modo tracking") — **aceita no fix
 * round 1**: a tabela `schedules` não tem campo de categoria (confirmado no
 * schema AQL) e o formulário nativo de criar agendado não pede uma, então
 * não existe mapeamento 1:1 schedule→orçamento sem heurística frágil. O
 * 🔒 de "ligar o modo tracking" deixa de valer pra esta coluna.
 *
 * ⚠️⚠️ FIX ROUND 1 (Finding 2, Important) — `getScheduledAmount` também
 * resolve o valor quando o dono escolhe "isbetween" (faixa: `_amount =
 * {num1, num2}`, como o próprio `ScheduleEditForm` produz), fazendo a
 * MÉDIA — sem ela, `Number({num1,num2})` vira `NaN` e o valor sumia como
 * zero, em silêncio, bem no caso da conta que varia mês a mês (o exemplo
 * "Luz" do próprio brief).
 *
 * Best-effort, mesmo espírito de `buscarContaDaIrmaOrfa` em
 * `useMesFinanceiro.ts`: uma falha aqui não pode derrubar o resumo do mês
 * (que já carregou por outro caminho) — degrada pra lista vazia, mas
 * EXPÕE o erro (ver `Retorno.erro`, Finding 3) pra a tela não confundir
 * "falhou" com "nunca cadastrou".
 */
export function useAgendadosDoMes(
  mes: string,
  linhasDoMes: LinhaDoMes[],
  escopo: Escopo,
): Retorno {
  const [agendados, setAgendados] = useState<AgendadoDoMes[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<Error | null>(null);

  // Agrupa as linhas do mês por agendado UMA VEZ — serve tanto pra achar
  // "quem já pagou" (`idsPagos`, candidatura da query) quanto pra decidir
  // escopo de agendado sem sinal na regra (Finding 1, passo 2).
  const linhasPorAgendado = new Map<string, LinhaDoMes[]>();
  for (const l of linhasDoMes) {
    if (l.schedule === null) continue;
    const lista = linhasPorAgendado.get(l.schedule) ?? [];
    lista.push(l);
    linhasPorAgendado.set(l.schedule, lista);
  }
  const idsPagos = [...linhasPorAgendado.keys()].sort();
  const idsPagosChave = idsPagos.join(',');

  // `useRef`, não uma constante fechada no `useEffect`: um ref é o jeito
  // que o próprio ESLint (`react-hooks/exhaustive-deps`) reconhece como
  // "não precisa entrar na lista de dependências" — ler `.current` dentro
  // do efeito sempre pega o valor mais atual (calculado acima, a cada
  // render), sem forçar o efeito a reexecutar quando `linhasPorAgendado`
  // muda de REFERÊNCIA mas não de CONTEÚDO relevante (é `idsPagosChave`,
  // abaixo, quem decide isso, com uma chave por VALOR — mesma técnica que
  // corrigiu o loop infinito medido na Task 10 original).
  const linhasPorAgendadoRef = useRef(linhasPorAgendado);
  linhasPorAgendadoRef.current = linhasPorAgendado;

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    const inicio = monthUtils.firstDayOfMonth(mes);
    const fim = monthUtils.lastDayOfMonth(mes);

    const mapaLinhasDesteEfeito = linhasPorAgendadoRef.current;
    const idsPagosDesteEfeito = [...mapaLinhasDesteEfeito.keys()];
    const filtroDoMes = { next_date: { $gte: inicio, $lte: fim } };
    const filtro =
      idsPagosDesteEfeito.length > 0
        ? { $or: [filtroDoMes, { id: { $oneof: idsPagosDesteEfeito } }] }
        : filtroDoMes;

    aqlQuery(
      q('schedules')
        .filter({ completed: false, ...filtro })
        .select(['id', 'name', 'next_date', '_amount', '_actions']),
    )
      .then(({ data }) => {
        if (cancelado) return;

        const brutos = (data ?? []) as Array<Record<string, unknown>>;
        setAgendados(
          brutos
            .filter(s => {
              const id = String(s.id);
              const notasDaRegra = notasDoAgendado(s._actions);
              const linhasDoAgendado = mapaLinhasDesteEfeito.get(id) ?? [];
              return agendadoPertenceAoEscopo(
                notasDaRegra,
                linhasDoAgendado,
                escopo,
              );
            })
            .map(s => ({
              id: String(s.id),
              nome: String(s.name ?? ''),
              // `next_date` é a data do PRÓXIMO vencimento — pra uma fixa
              // ainda NÃO paga é o vencimento deste mês (uma vez paga, o
              // Actual já empurra pro mês seguinte; ver o ⚠️⚠️ de
              // "candidato ao mês" acima). É exatamente essa combinação
              // (não paga + data no passado) que `motor/filaPendencias.ts`
              // lê como "conta fixa vencida".
              vencimento: s.next_date ? String(s.next_date) : null,
              valorPrevisto: Math.abs(
                getScheduledAmount(
                  s._amount as number | { num1: number; num2: number },
                ),
              ),
            })),
        );
        setCarregando(false);
      })
      .catch((e: unknown) => {
        // Best-effort quanto ao RESUMO do mês (Heroi/TresColunas já
        // carregaram por outra consulta e não podem ser derrubados por
        // esta) — mas NÃO quanto à TELA: `erro` fica exposto pra
        // `ContasDoMes.tsx` avisar, em vez de mostrar a instrução de
        // cadastro pra quem já cadastrou (Finding 3).
        if (cancelado) return;
        setAgendados([]);
        setErro(e instanceof Error ? e : new Error(String(e)));
        setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [mes, idsPagosChave, escopo]);

  return { agendados, carregando, erro };
}
